import { Loader2, Edit3, Trash2, CheckCircle2, Clock, BellRing } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ReflectedToggle } from "./ReflectedToggle";

/**
 * InstallmentModal — the "Registrar abono" dialog. Extracted from
 * AdminDashboard.jsx to keep that file lean.
 *
 * Props:
 *   plan: current payment plan (null → dialog closed)
 *   form: { amount, method, notes, receipt_file }
 *   setForm: setter for form
 *   busy: bool — disables the submit button
 *   fmt: (n, currency) => string      — currency formatter from parent
 *   onClose: () => void
 *   onSubmit: () => void              — calls addInstallment in parent
 *   onEditInstallment: (plan, inst) => void
 *   onDeleteInstallment: (planId, instId) => void
 */
export const InstallmentModal = ({
  plan,
  form,
  setForm,
  busy,
  fmt,
  onClose,
  onSubmit,
  onEditInstallment,
  onDeleteInstallment,
  onMarkReflected,
}) => {
  const effectiveTotal = Number(plan?.agreed_price_after_offer ?? plan?.agreed_price ?? 0);
  return (
    <Dialog open={!!plan} onOpenChange={onClose}>
      <DialogContent className="bg-[#0a0a0a] border border-[#d4af37]/30 text-white max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif">
            Registrar abono — <span className="text-sm text-neutral-400">{plan?.service_name}</span>
          </DialogTitle>
        </DialogHeader>
        {plan && (
          <div className="space-y-3">
            <div className="text-xs bg-black/40 border border-white/10 rounded p-3">
              <div className="flex justify-between mb-1">
                <span className="text-neutral-400">Total:</span>
                <span className="text-white">{fmt(effectiveTotal, plan.currency)}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span className="text-neutral-400">Pagado:</span>
                <span className="text-emerald-300">{fmt(plan.paid_total, plan.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-400">Resta:</span>
                <span className="text-amber-300 font-semibold">
                  {fmt(Math.max(0, effectiveTotal - plan.paid_total), plan.currency)}
                </span>
              </div>
            </div>
            <div>
              <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Monto recibido</Label>
              <Input
                data-testid="install-amount"
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <div>
              <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Método</Label>
              <Input
                data-testid="install-method"
                value={form.method}
                onChange={(e) => setForm({ ...form, method: e.target.value })}
                placeholder="Ej: Transferencia, Efectivo"
                className="bg-black/40 border-white/10 text-white"
              />
            </div>
            <Textarea
              data-testid="install-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Comentario / notas del pago (visible al cliente)"
              className="bg-black/40 border-white/10 text-white"
            />
            <ReflectedToggle
              value={form.reflected !== false}
              onChange={(v) => setForm({ ...form, reflected: v })}
              idPrefix="install-reflected"
            />
            <div>
              <Label className="text-neutral-300 mb-1.5 text-xs uppercase tracking-widest">Comprobante (opcional)</Label>
              <p className="text-[10px] text-neutral-500 mb-1.5">
                Imagen o PDF del ticket / transferencia — se archiva junto a este abono.
              </p>
              <input
                data-testid="install-receipt-file"
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => setForm({ ...form, receipt_file: e.target.files?.[0] || null })}
                className="text-xs text-neutral-300 bg-black/40 border border-white/10 rounded w-full px-2 py-1.5 file:mr-2 file:text-[11px] file:px-2 file:py-0.5 file:rounded file:bg-sky-500/10 file:border file:border-sky-500/30 file:text-sky-300"
              />
              {form.receipt_file && (
                <div className="text-[10px] text-emerald-300 mt-1 truncate" title={form.receipt_file.name}>
                  📎 {form.receipt_file.name}
                </div>
              )}
            </div>
            <button
              data-testid="install-save"
              onClick={onSubmit}
              disabled={busy}
              className="btn-gold w-full py-3 rounded-full uppercase text-xs tracking-widest disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Registrando…
                </>
              ) : (
                "Registrar abono y notificar"
              )}
            </button>

            {plan.installments?.length > 0 && (
              <div className="border-t border-white/10 pt-3">
                <div className="text-xs text-neutral-400 uppercase tracking-widest mb-2">Historial</div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {plan.installments.map((i) => {
                    const pending = i.reflected === false;
                    return (
                    <div
                      key={i.id}
                      data-testid={`install-row-${i.id}`}
                      className={`flex justify-between items-center text-xs rounded px-2 py-1.5 gap-2 border ${pending ? "bg-amber-500/[0.07] border-amber-500/30" : "bg-black/30 border-white/5"}`}
                    >
                      <div className="min-w-0 flex-1">
                        <span className="text-emerald-300 font-semibold">{fmt(i.amount, plan.currency)}</span>
                        {i.method && <span className="text-neutral-400 ml-2">· {i.method}</span>}
                        <span
                          data-testid={`install-reflected-badge-${i.id}`}
                          className={`ml-2 inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ${pending ? "bg-amber-500/20 text-amber-200 border border-amber-400/40" : "bg-emerald-500/15 text-emerald-300"}`}
                        >
                          {pending ? <><Clock className="w-2.5 h-2.5" /> Sin reflejar</> : <><CheckCircle2 className="w-2.5 h-2.5" /> Reflejado</>}
                        </span>
                        <div className="text-[10px] text-neutral-500">
                          {new Date(i.at).toLocaleString()}
                          {i.updated_at && <span className="text-amber-400/70 ml-1">(editado)</span>}
                          {!pending && i.reflected_at && i.reflected_at.slice(0, 16) !== (i.at || "").slice(0, 16) && (
                            <span className="text-emerald-400/70 ml-1">· reflejado {new Date(i.reflected_at).toLocaleDateString()}</span>
                          )}
                        </div>
                        {i.notes && (
                          <div className="text-[10px] text-neutral-500 italic truncate" title={i.notes}>📝 {i.notes}</div>
                        )}
                        {pending && onMarkReflected && (
                          <button
                            data-testid={`install-mark-reflected-${i.id}`}
                            onClick={() => onMarkReflected(plan, i)}
                            className="mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded border border-emerald-400/60 bg-emerald-500/15 text-emerald-200 hover:bg-emerald-500/25 font-semibold"
                            title="Marcar este abono como reflejado y avisar al cliente"
                          >
                            <BellRing className="w-3 h-3" /> Marcar reflejado y avisar
                          </button>
                        )}
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <button
                          data-testid={`install-edit-${i.id}`}
                          onClick={() => onEditInstallment(plan, i)}
                          title="Editar este abono"
                          className="text-sky-400 hover:text-sky-300 p-1"
                        >
                          <Edit3 className="w-3 h-3" />
                        </button>
                        <button
                          data-testid={`install-delete-${i.id}`}
                          onClick={() => onDeleteInstallment(plan.id, i.id)}
                          title="Eliminar este abono"
                          className="text-red-400 hover:text-red-300 p-1"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InstallmentModal;
